import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './hooks/useAuth';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import { InternalRoute } from './components/auth/InternalRoute';
import { HomeRedirect } from './components/auth/HomeRedirect';
import { AppLayout } from './components/layout/AppLayout';
import { Login } from './pages/auth/Login';
import { Activate } from './pages/auth/Activate';
import { AddressValidationPage } from './pages/tickets/AddressValidationPage';
import { AddressValidationQueue } from './pages/validation/AddressValidationQueue';
import { MduRequestQueue } from './pages/mdu/MduRequestQueue';
import { MduRequestDetail } from './pages/mdu/MduRequestDetail';
import { Documentation } from './pages/docs/Documentation';
import { NotesPage } from './pages/notes/NotesPage';
import { UserManagement } from './pages/admin/UserManagement';
import { HandoffSettings } from './pages/admin/HandoffSettings';
import { ActionLog } from './pages/admin/ActionLog';
import { JvidRegistry } from './pages/admin/JvidRegistry';
import { BlacklistFeedback } from './pages/admin/BlacklistFeedback';
import { ValidationStats } from './pages/admin/ValidationStats';
import { MappingTable } from './pages/admin/MappingTable';
import { DmpConsolePage } from './pages/dmp/DmpConsolePage';
import { RenumberingPage } from './pages/renumbering/RenumberingPage';

// Standalone Address-Validation + user-management routes. Every authenticated user is an internal
// Unifiber user (Admin or Validateur); the admin screens are gated by AdminRoute (SYSTEM_ADMIN).
export function App() {
  return (
    <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '') || '/'}>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/activate" element={<Activate />} />

          {/* Protected */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              {/* Ouverte à tous les rôles, BUILD compris — c'est sa seule page (user 2026-08-07). */}
              <Route path="/dmp-console" element={<DmpConsolePage />} />
              {/* Validation — Admin + Validateur. Un compte Build est renvoyé vers sa console. */}
              <Route element={<InternalRoute />}>
                <Route path="/address-validations" element={<AddressValidationQueue />} />
                <Route path="/tickets/:id/address-validation" element={<AddressValidationPage />} />
                <Route path="/mdu-requests" element={<MduRequestQueue />} />
                <Route path="/mdu-requests/:id" element={<MduRequestDetail />} />
                <Route path="/documentation" element={<Documentation />} />
                {/* Notes perso — SOUS InternalRoute, donc admins et validateurs, mais PAS le rôle
                    BUILD : il n'atteint que la console DMP, ce qui est conforme à la demande. */}
                <Route path="/notes" element={<NotesPage />} />
                {/* Admin only */}
                <Route element={<AdminRoute />}>
                  {/* Renumbering (H-1) : un lot touche des centaines d'adresses d'un coup — ce n'est
                      pas le geste d'un validateur. Hors de /admin : c'est du travail métier, pas un
                      réglage de l'application. */}
                  <Route path="/renumbering" element={<RenumberingPage />} />
                  <Route path="/admin/users" element={<UserManagement />} />
                  <Route path="/admin/handoff-settings" element={<HandoffSettings />} />
                  {/* Old paths kept → the same combined page (so existing links still resolve). */}
                  <Route path="/admin/escalation-settings" element={<HandoffSettings />} />
                  <Route path="/admin/build-settings" element={<HandoffSettings />} />
                  <Route path="/admin/mapping-table" element={<MappingTable />} />
                  <Route path="/admin/action-log" element={<ActionLog />} />
                  <Route path="/admin/jvid-registry" element={<JvidRegistry />} />
                  <Route path="/admin/blacklist-feedback" element={<BlacklistFeedback />} />
                  <Route path="/admin/validation-stats" element={<ValidationStats />} />
                </Route>
              </Route>
            </Route>
          </Route>

          {/* Default redirect → the queue */}
          <Route path="/" element={<HomeRedirect />} />
          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
